import React from 'react';
import { motion } from 'framer-motion';

const sectionHeading = "text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4";

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.h2
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    viewport={{ once: false, amount: 0.5 }}
    className={sectionHeading}
  >
    {children}
  </motion.h2>
);

const PrivacyPolicy: React.FC = () => (
  <motion.section
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    className="w-full overflow-x-hidden"
    style={{ maxWidth: 800, margin: '0 auto', padding: '2rem' }}
  >
    <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-6">
      Privacy Policy
    </h1>
    <p className="mb-4">
      <strong>Effective Date:</strong> September 9, 2025 <br />
      <strong>Website:</strong> rkandkimport.com
    </p>

    <p className="mb-8">
      At <span className="font-semibold">RK &amp; K Import</span>, your
      privacy matters to us. This Privacy Policy explains what information we
      collect and how we use it.
    </p>

    <SectionHeading>Information We Collect</SectionHeading>
    <ul className="list-disc list-inside mb-8 space-y-2">
      <li>
        <strong>Contact Form Submissions:</strong> If you fill out and submit
        our contact form, we collect the information you provide (e.g., name,
        email, phone number, and your message).
      </li>
      <li>
        <strong>Analytics Data:</strong> We collect non-personal information
        automatically through third-party services such as Google Analytics
        and Vercel Analytics. This may include IP address, browser type,
        device information, pages visited, and time spent on the site.
      </li>
    </ul>

    <SectionHeading>How We Use Your Information</SectionHeading>
    <ul className="list-disc list-inside mb-8 space-y-2">
      <li>To respond to inquiries submitted via the contact form.</li>
      <li>To monitor website performance and visitor counts.</li>
      <li>
        To improve user experience and understand traffic sources for
        advertising (e.g., Google Ads).
      </li>
    </ul>
    <p className="mb-8">
      We do <strong>not</strong> sell or share your personal information with
      outside parties, except as required by law or to operate our services.
    </p>

    <SectionHeading>Cookies &amp; Analytics</SectionHeading>
    <ul className="list-disc list-inside mb-8 space-y-2">
      <li>
        <strong>Google Analytics</strong> – helps measure website traffic and
        Google Ads performance.
      </li>
      <li>
        <strong>Vercel Analytics</strong> – helps monitor visitor counts and
        site performance.
      </li>
    </ul>
    <p className="mb-8">
      These services may use cookies or similar technologies to collect
      anonymous usage data. You can disable cookies in your browser settings
      if you prefer.
    </p>

    <SectionHeading>Data Retention</SectionHeading>
    <ul className="list-disc list-inside mb-8 space-y-2">
      <li>
        <strong>Contact form data</strong> is retained only as long as
        necessary to respond to your request.
      </li>
      <li>
        <strong>Analytics data</strong> is stored by the respective providers
        according to their policies.
      </li>
    </ul>

    <SectionHeading>Third-Party Links</SectionHeading>
    <p className="mb-8">
      Our website may contain links to external sites. We are not responsible
      for the content or privacy practices of those websites.
    </p>

    <SectionHeading>Changes to This Policy</SectionHeading>
    <p className="mb-8">
      We may update this Privacy Policy from time to time. Updates will be
      posted on this page with a new effective date.
    </p>

    <SectionHeading>Contact Us</SectionHeading>
    <p>
      If you have any questions about this Privacy Policy, contact us at:{" "}
      <br />
      <span className="font-semibold">Email:</span>{" "}
      <a
        href="mailto:operations@rkandkimport.com"
        className="text-blue-600 dark:text-blue-400 hover:underline"
      >
        info@rkandkimport.com
      </a>
    </p>
  </motion.section>
);

export default PrivacyPolicy;
